// AUTO-GENERATED from docs/TECHNICAL_PRD.md — CID Agent PWA API layer
// Module: Earnings (commission breakdown, unlocked/paid/pending, payout ledger, overrides, clawbacks, statements)
// Every list (collection GET) takes params { page, limit, ...filters }.
import client from './client';

/**
 * GET /api/agent/earnings/summary [LIST · paginated]
 * Top-of-screen earnings KPIs for the caller: lifetime & current-year GCI, gci_tier, total entitled/unlocked/paid/pending across all my commission lines, split into personal vs override buckets, plus my ledger remaining_balance and pending-clawback total.
 * @disposition BUILD_NEW · maps to NEW (aggregates listLedger + listEntitledCommissions + agents GCI fields)
 */
export const listEarningsSummary = (params = {}) => client.get(`/api/agent/earnings/summary`, { params });

/**
 * GET /api/agent/earnings/summary/period?from=&to= (or ?month=YYYY-MM)
 * Period-scoped KPIs from the caller's own commission lines dated within the range:
 * { from, to, earned, released, paid, pending, personal, override, deals, payouts }.
 */
export const getEarningsPeriodSummary = (params = {}) => client.get(`/api/agent/earnings/summary/period`, { params });

/**
 * GET /api/agent/earnings/monthly?year=YYYY
 * The caller's twelve-month earnings breakdown for one calendar year —
 * { year, years[], months: [{ month, label, commission, override, total, deals }],
 * totals: { commission, override, total, deals } }. Backs the Total earning block.
 * Months are keyed on the SALE's date, the same basis as the period tiles, so a
 * whole year here equals /summary/period's `earned` for that year.
 */
export const getEarningsMonthly = (params = {}) => client.get(`/api/agent/earnings/monthly`, { params });

/**
 * GET /api/agent/earnings/statement?from=&to= (or ?month=YYYY-MM)
 * Full period statement document — { agent, period, commissions[], overrides[], payouts[], totals }.
 * Backs the in-app statement preview.
 */
export const getEarningsStatement = (params = {}) => client.get(`/api/agent/earnings/statement`, { params });

/**
 * GET /api/agent/earnings/statement.csv?from=&to= — the same statement as a CSV
 * string (text/csv). Fetched as raw text; the caller triggers the browser download.
 */
export const getEarningsStatementCsv = (params = {}) =>
  client.get(`/api/agent/earnings/statement.csv`, { params, responseType: 'text' });

/**
 * GET /api/agent/earnings/by-sale [LIST · paginated]
 * My commission breakdown grouped per sale — the primary earnings list. Each sale shows my personal + override + GAP lines with unlocked/paid/pending/locked and the sale's knockoff collection fraction.
 * @disposition ADAPT_SCOPE · maps to listEntitledCommissions (payout.service.js:240)
 */
export const listEarningsBySale = (params = {}) => client.get(`/api/agent/earnings/by-sale`, { params });

/**
 * GET /api/agent/earnings/sales/:saleId
 * Full commission breakdown for one sale — only the caller's own lines, with per-line personal/override-by-gen/GAP detail and unlocked-vs-paid-vs-pending + knockoff fraction. Backs the earnings detail drawer.
 * @disposition BUILD_NEW · maps to NEW (self-filtered read of sale_commissions + sales)
 */
export const getEarningsSalesBySaleId = (saleId, params = {}) => client.get(`/api/agent/earnings/sales/${saleId}`, { params });

/**
 * GET /api/agent/earnings/overrides [LIST · paginated]
 * My override income per downline deal — dedicated view of override/GAP/project-leader/PIC earnings, each attributed to the downline seller and generation that produced it.
 * @disposition BUILD_NEW · maps to NEW (self-filtered sale_commissions joined to sale_agents→agents)
 */
export const listEarningsOverrides = (params = {}) => client.get(`/api/agent/earnings/overrides`, { params });

/**
 * GET /api/agent/earnings/ledger [LIST · paginated]
 * My single payout ledger status — remaining_balance, total_paid, total_due, total_clawback, status. No id in URL.
 * @disposition ADAPT_SCOPE · maps to getLedgerEntry / listLedger (payout.service.js:155 / :10)
 */
export const listEarningsLedger = (params = {}) => client.get(`/api/agent/earnings/ledger`, { params });

/**
 * GET /api/agent/earnings/payouts?page=&limit= [LIST · paginated]
 * Every payout on my ledger, newest first — reversed ones included (status
 * REVERSED, their voucher voided). Each row: { id, payout_date, amount,
 * clawback_offset, net, status, reversed_at, channel, voucher: null |
 * { id, doc_no, has_file, voided } }.
 */
export const listEarningsPayouts = (params = {}, config = {}) =>
  client.get(`/api/agent/earnings/payouts`, { params, ...config });

/**
 * GET /api/agent/earnings/payouts/:id
 * One of my payouts — the list row plus `reversal_reason` and `lines` (what it
 * paid: { sale_id, sale_ref, property, commission_type, amount }). 404 for a
 * payout that is not mine.
 */
export const getEarningsPayout = (payoutId) => client.get(`/api/agent/earnings/payouts/${payoutId}`);

/**
 * GET /api/agent/earnings/payouts/:id/voucher
 * A 10-minute signed link to the payout's payment voucher: { url, doc_no,
 * file_name, voided }. 404 while HQ has not attached one with a file.
 */
export const getEarningsPayoutVoucher = (payoutId) => client.get(`/api/agent/earnings/payouts/${payoutId}/voucher`);

/**
 * GET /api/agent/earnings/erp213-vouchers?page=&limit= [LIST · paginated]
 * My payment vouchers from ERP213, before CIDAS — history only, already counted in
 * Paid. Rows: { id, pv_no, pv_date, amount, wht_amount, sales: [{ id, sale_ref }],
 * has_file }; page 1 also carries `summary: { count, total_amount }`.
 */
export const listErp213Vouchers = (params = {}, config = {}) =>
  client.get(`/api/agent/earnings/erp213-vouchers`, { params, ...config });

/**
 * GET /api/agent/earnings/erp213-vouchers/:id
 * One of my ERP213 vouchers: header, bank (last 4 digits only), lines, files. 404 if not mine.
 */
export const getErp213Voucher = (id) => client.get(`/api/agent/earnings/erp213-vouchers/${id}`);

/** GET …/erp213-vouchers/:id/files/:fileId — a 10-minute link to one of its files: { url, file_name }. */
export const getErp213VoucherFile = (id, fileId) => client.get(`/api/agent/earnings/erp213-vouchers/${id}/files/${fileId}`);

/**
 * GET /api/agent/earnings/adjustments [LIST · paginated]
 * Clawback/adjustment VISIBILITY (read only) — amounts the agent may owe back from reverted/voided sales, with recovery status.
 * @disposition ADAPT_SCOPE · maps to listAdjustments (payout.service.js:829)
 */
export const listEarningsAdjustments = (params = {}) => client.get(`/api/agent/earnings/adjustments`, { params });

/**
 * GET /api/agent/earnings/statements [LIST · paginated]
 * My statement / payout history — each disbursement event with gross, clawback offset, net disbursement, cheque number, date and its per-sale allocations.
 * @disposition ADAPT_SCOPE · maps to getLedgerEntry.triggers enrichment + getTrigger (payout.service.js:191 / :481)
 */
export const listEarningsStatements = (params = {}) => client.get(`/api/agent/earnings/statements`, { params });

/**
 * GET /api/agent/earnings/statements/:triggerId
 * Single statement detail with full allocation lines and (optionally) signed download URLs for the caller's own payout evidence.
 * @disposition ADAPT_SCOPE · maps to getTrigger + getAttachmentUrls (payout.service.js:481 / :1110)
 */
export const getEarningsStatementsByTriggerId = (triggerId, params = {}) => client.get(`/api/agent/earnings/statements/${triggerId}`, { params });

/**
 * GET /api/agent/earnings/statements/export [LIST · paginated]
 * Export my statement/earnings history as CSV.
 * @disposition ADAPT_SCOPE · maps to exportPayouts (payout.service.js:971)
 */
export const listEarningsStatementsExport = (params = {}) => client.get(`/api/agent/earnings/statements/export`, { params });
